import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Save, Route, Play, Loader2, AlertTriangle, CheckCircle2, XCircle, Sparkles, Wand2 } from "lucide-react";
import { useRoutingRules, useResponsePolicies, useSkills, useCurrentOrgId, type RoutingRule, type ResponsePolicy } from "@/hooks/useSkillOS";

const INTENTS = ["question", "command", "complaint", "greeting", "task", "info", "analysis", "summary_request", "sales", "support", "poll", "welcome", "farewell", "escalation", "poll_request", "task_assign", "other"];
const TYPOLOGIES = ["all", "suporte", "vendas", "comunidade", "projeto", "interno"];
const TASK_CLASSES = ["fast", "balanced", "reasoning", "retrieval", "vision"];

// Default skill mapping (used when "Sincronizar Intents" creates missing rules)
const DEFAULT_INTENT_SKILL: Record<string, { skill: string; task: string }> = {
  question: { skill: "faq-responder", task: "retrieval" },
  command: { skill: "conversational", task: "balanced" },
  complaint: { skill: "support", task: "balanced" },
  greeting: { skill: "conversational", task: "fast" },
  task: { skill: "conversational", task: "balanced" },
  info: { skill: "conversational", task: "fast" },
  analysis: { skill: "analyzer", task: "reasoning" },
  summary_request: { skill: "summary", task: "balanced" },
  sales: { skill: "sales-assistant", task: "balanced" },
  support: { skill: "support", task: "balanced" },
  poll: { skill: "poll", task: "balanced" },
  welcome: { skill: "conversational", task: "fast" },
  farewell: { skill: "conversational", task: "fast" },
  escalation: { skill: "support", task: "balanced" },
  poll_request: { skill: "poll", task: "balanced" },
  task_assign: { skill: "conversational", task: "balanced" },
  other: { skill: "conversational", task: "balanced" },
};

export default function RoutingConfig() {
  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Configuração de Roteamento" }]} />
      <div>
        <h1 className="text-2xl font-bold text-foreground font-headline flex items-center gap-2">
          <Route className="w-6 h-6" /> Routing Config
        </h1>
        <p className="text-muted-foreground text-sm">Defina como intents são roteadas e como o agente responde por tipologia.</p>
      </div>

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules">Regras de Skill</TabsTrigger>
          <TabsTrigger value="policies">Políticas de Resposta</TabsTrigger>
        </TabsList>
        <TabsContent value="rules" className="mt-4"><RulesTab /></TabsContent>
        <TabsContent value="policies" className="mt-4"><PoliciesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function RulesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: rules = [], isLoading } = useRoutingRules();
  const { data: skills = [] } = useSkills();
  const { data: orgId } = useCurrentOrgId();

  const skillSet = useMemo(() => {
    const s = new Map<string, boolean>();
    skills.forEach((sk) => s.set(sk.name, sk.active));
    return s;
  }, [skills]);

  const missingIntents = useMemo(() => {
    const covered = new Set(rules.filter((r) => r.typology === "all").map((r) => r.intent));
    return INTENTS.filter((i) => !covered.has(i));
  }, [rules]);

  const updateMut = useMutation({
    mutationFn: async (r: RoutingRule) => {
      const { error } = await supabase
        .from("skill_routing_rules")
        .update({ skill_name: r.skill_name, task_class: r.task_class, priority: r.priority, active: r.active })
        .eq("id", r.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["skill-os", "routing-rules"] }); toast({ title: "Regra atualizada" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("skill_routing_rules").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["skill-os", "routing-rules"] }); toast({ title: "Regra removida" }); },
  });

  const createMut = useMutation({
    mutationFn: async (payload: { intent: string; typology: string; skill_name: string; task_class: string; priority: number }) => {
      if (!orgId) throw new Error("Sem organização");
      const { error } = await supabase.from("skill_routing_rules").insert({ ...payload, org_id: orgId });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["skill-os", "routing-rules"] }); toast({ title: "Regra criada" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const syncMut = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Sem organização");
      if (missingIntents.length === 0) return 0;
      const payloads = missingIntents.map((intent) => {
        const def = DEFAULT_INTENT_SKILL[intent] ?? { skill: "conversational", task: "balanced" };
        return { org_id: orgId, intent, typology: "all", skill_name: def.skill, task_class: def.task, priority: 0 };
      });
      const { error } = await supabase.from("skill_routing_rules").insert(payloads);
      if (error) throw error;
      return payloads.length;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ["skill-os", "routing-rules"] });
      toast({ title: `${n} regra(s) criada(s)`, description: "Intents sem cobertura foram sincronizados com defaults." });
    },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-14" />)}</div>;

  if (rules.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <Route className="w-7 h-7 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-1">Nenhuma regra de roteamento ainda</h3>
          <p className="text-sm text-muted-foreground max-w-md mb-6">
            Regras vinculam <strong>intents</strong> (o que o usuário quer) a <strong>skills</strong> (como a SYRA responde),
            opcionalmente filtrando por <strong>tipologia</strong> de grupo. Sem regras, todas as mensagens caem no skill default.
          </p>
          <div className="flex gap-2">
            <Button onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
              {syncMut.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
              Sincronizar com defaults
            </Button>
            <CreateRuleDialog skills={skills.map(s => s.name)} onCreate={(p) => createMut.mutate(p)} />
          </div>
        </CardContent>
      </Card>
    );
  }

  const grouped = TYPOLOGIES.map((t) => ({ typology: t, rules: rules.filter(r => r.typology === t) }));

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <TestRoutingDialog />
        {missingIntents.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => syncMut.mutate()} disabled={syncMut.isPending}>
            {syncMut.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />}
            Sincronizar Intents ({missingIntents.length})
          </Button>
        )}
        <CreateRuleDialog skills={skills.map(s => s.name)} onCreate={(p) => createMut.mutate(p)} />
      </div>

      {missingIntents.length > 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" /> Intents sem regra ({missingIntents.length})
            </CardTitle>
            <CardDescription className="text-xs">
              Estes intents serão tratados pelo skill default. Clique em "Sincronizar Intents" para criar regras com defaults seguros.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-1">
              {missingIntents.map((i) => <Badge key={i} variant="outline" className="text-xs">{i}</Badge>)}
            </div>
          </CardContent>
        </Card>
      )}

      {grouped.map(({ typology, rules: tRules }) => tRules.length > 0 && (
        <Card key={typology}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Badge variant="outline" className="capitalize">{typology}</Badge>
              <span className="text-muted-foreground font-normal">({tRules.length} regras)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {tRules.map((r) => (
              <RuleRow key={r.id} rule={r} skills={skills.map(s => s.name)} skillSet={skillSet} onSave={updateMut.mutate} onDelete={deleteMut.mutate} />
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RuleRow({ rule, skills, skillSet, onSave, onDelete }: { rule: RoutingRule; skills: string[]; skillSet: Map<string, boolean>; onSave: (r: RoutingRule) => void; onDelete: (id: string) => void }) {
  const [local, setLocal] = useState(rule);
  const dirty = JSON.stringify(local) !== JSON.stringify(rule);
  const skillExists = skillSet.has(local.skill_name);
  const skillActive = skillSet.get(local.skill_name) === true;

  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1.5 px-1 hover:bg-muted/50 rounded">
      <div className="col-span-2"><Badge variant="secondary" className="text-xs">{local.intent}</Badge></div>
      <Select value={local.skill_name} onValueChange={(v) => setLocal({ ...local, skill_name: v })}>
        <SelectTrigger className="col-span-3 h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{skills.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
      <div className="col-span-1 flex justify-center" title={!skillExists ? "Skill não existe" : skillActive ? "Skill ativo" : "Skill inativo"}>
        {!skillExists ? <XCircle className="w-4 h-4 text-destructive" /> : skillActive ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <AlertTriangle className="w-4 h-4 text-amber-500" />}
      </div>
      <Select value={local.task_class} onValueChange={(v) => setLocal({ ...local, task_class: v })}>
        <SelectTrigger className="col-span-2 h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>{TASK_CLASSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
      </Select>
      <Input type="number" value={local.priority} onChange={(e) => setLocal({ ...local, priority: parseInt(e.target.value) || 0 })} className="col-span-1 h-8 text-xs" />
      <div className="col-span-1"><Switch checked={local.active} onCheckedChange={(c) => setLocal({ ...local, active: c })} /></div>
      <div className="col-span-2 flex gap-1 justify-end">
        <Button size="sm" variant="outline" disabled={!dirty} onClick={() => onSave(local)}><Save className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(local.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
      </div>
    </div>
  );
}

function TestRoutingDialog() {
  const { data: orgId } = useCurrentOrgId();
  const { data: rules = [] } = useRoutingRules();
  const { data: skills = [] } = useSkills();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [typology, setTypology] = useState("all");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    intent: string; confidence: number; rule?: RoutingRule; skill?: { name: string; active: boolean; execution_type: string };
  } | null>(null);

  const run = async () => {
    if (!orgId || !text.trim()) return;
    setLoading(true); setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("intent-classify", {
        body: { content: text, org_id: orgId },
      });
      if (error) throw error;
      const intent = data?.intent ?? "other";
      const confidence = data?.confidence ?? 0;

      // Lookup rule: matching intent, typology in (selected, 'all'), highest priority
      const candidates = rules.filter((r) => r.intent === intent && r.active && (r.typology === typology || r.typology === "all"));
      candidates.sort((a, b) => b.priority - a.priority || (a.typology === typology ? -1 : 1));
      const rule = candidates[0];

      const skill = rule ? skills.find((s) => s.name === rule.skill_name) : undefined;
      setResult({
        intent,
        confidence,
        rule,
        skill: skill ? { name: skill.name, active: skill.active, execution_type: (skill as { execution_type?: string }).execution_type ?? "hardcoded" } : undefined,
      });
    } catch (e) {
      setResult({ intent: "error", confidence: 0 });
      console.error(e);
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"><Play className="w-3 h-3 mr-1" /> Testar Roteamento</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Simular roteamento de mensagem</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Mensagem de teste</label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder='Ex: "vamos fazer uma votação?" ou "preciso de ajuda urgente"' />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Tipologia do grupo</label>
            <Select value={typology} onValueChange={setTypology}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPOLOGIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={run} disabled={loading || !text.trim()} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            Classificar e rotear
          </Button>

          {result && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-md text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Intent classificado</span>
                <Badge variant="default">{result.intent} <span className="ml-1 opacity-70">({(result.confidence * 100).toFixed(0)}%)</span></Badge>
              </div>
              {result.rule ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">Regra escolhida</span>
                    <Badge variant="outline">{result.rule.typology} · prio {result.rule.priority}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">Skill executado</span>
                    {result.skill ? (
                      <Badge variant={result.skill.active ? "default" : "secondary"}>
                        {result.skill.name} · {result.skill.execution_type}
                      </Badge>
                    ) : (
                      <Badge variant="destructive">{result.rule.skill_name} (não encontrado)</Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-xs">Task class</span>
                    <Badge variant="outline" className="text-xs">{result.rule.task_class}</Badge>
                  </div>
                </>
              ) : (
                <div className="text-xs text-amber-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Nenhuma regra ativa para este intent — fallback para conversational.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CreateRuleDialog({ skills, onCreate }: { skills: string[]; onCreate: (p: { intent: string; typology: string; skill_name: string; task_class: string; priority: number }) => void }) {
  const [open, setOpen] = useState(false);
  const [intent, setIntent] = useState("question");
  const [typology, setTypology] = useState("all");
  const [skill, setSkill] = useState(skills[0] ?? "");
  const [task, setTask] = useState("balanced");
  const [priority, setPriority] = useState(0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm"><Plus className="w-4 h-4 mr-1" /> Nova Regra</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nova Regra de Roteamento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs">Intent</label>
              <Select value={intent} onValueChange={setIntent}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{INTENTS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <label className="text-xs">Tipologia</label>
              <Select value={typology} onValueChange={setTypology}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPOLOGIES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <label className="text-xs">Skill</label>
              <Select value={skill} onValueChange={setSkill}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{skills.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <label className="text-xs">Task Class</label>
              <Select value={task} onValueChange={setTask}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TASK_CLASSES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
            <div>
              <label className="text-xs">Prioridade (maior = vence)</label>
              <Input type="number" value={priority} onChange={(e) => setPriority(parseInt(e.target.value) || 0)} />
            </div>
          </div>
          <Button onClick={() => { onCreate({ intent, typology, skill_name: skill, task_class: task, priority }); setOpen(false); }} disabled={!skill} className="w-full">Criar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PoliciesTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: policies = [], isLoading } = useResponsePolicies();

  const updateMut = useMutation({
    mutationFn: async (p: ResponsePolicy) => {
      const { error } = await supabase
        .from("response_policies")
        .update({
          formality: p.formality, depth: p.depth, max_response_chars: p.max_response_chars,
          use_list_format: p.use_list_format, allow_silence: p.allow_silence, tone: p.tone, active: p.active,
        })
        .eq("id", p.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["skill-os", "response-policies"] }); toast({ title: "Política atualizada" }); },
    onError: (e: Error) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  if (isLoading) return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-72" />)}</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {policies.map((p) => <PolicyCard key={p.id} policy={p} onSave={updateMut.mutate} />)}
    </div>
  );
}

function PolicyCard({ policy, onSave }: { policy: ResponsePolicy; onSave: (p: ResponsePolicy) => void }) {
  const [local, setLocal] = useState(policy);
  const dirty = JSON.stringify(local) !== JSON.stringify(policy);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <Badge variant="outline" className="capitalize">{local.typology}</Badge>
          <Switch checked={local.active} onCheckedChange={(c) => setLocal({ ...local, active: c })} />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="flex justify-between text-xs mb-1"><span>Formalidade</span><span>{local.formality.toFixed(2)}</span></div>
          <Slider value={[local.formality]} onValueChange={([v]) => setLocal({ ...local, formality: v })} min={0} max={1} step={0.05} />
        </div>
        <div>
          <label className="text-xs">Profundidade</label>
          <Select value={local.depth} onValueChange={(v) => setLocal({ ...local, depth: v })}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{["brief", "moderate", "detailed"].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs">Max chars: {local.max_response_chars}</label>
          <Slider value={[local.max_response_chars]} onValueChange={([v]) => setLocal({ ...local, max_response_chars: v })} min={200} max={3000} step={100} />
        </div>
        <div className="flex items-center gap-4 text-xs">
          <label className="flex items-center gap-2"><Switch checked={local.use_list_format} onCheckedChange={(c) => setLocal({ ...local, use_list_format: c })} /> Listas</label>
          <label className="flex items-center gap-2"><Switch checked={local.allow_silence} onCheckedChange={(c) => setLocal({ ...local, allow_silence: c })} /> Permitir silêncio</label>
        </div>
        <div>
          <label className="text-xs">Tom</label>
          <Textarea value={local.tone} onChange={(e) => setLocal({ ...local, tone: e.target.value })} rows={2} className="text-xs" />
        </div>
        <Button size="sm" disabled={!dirty} onClick={() => onSave(local)} className="w-full"><Save className="w-3 h-3 mr-1" /> Salvar</Button>
      </CardContent>
    </Card>
  );
}
